import { sanitizeText } from '@shared/utils/security';

export class UIStateManager {
  private element: HTMLElement;
  private statusManager: any;

  constructor(panelElement: HTMLElement, statusManager: any) {
    this.element = panelElement;
    this.statusManager = statusManager;
  }

  updateUIStateForElementSelection(hasElement: boolean): void {
    if (this.statusManager.isErrorState()) {
      return;
    }

    const actionSection =
      this.element.querySelector('[data-ce-wrap="value"]')?.parentElement?.parentElement;

    if (!hasElement) {
      if (actionSection) {
        (actionSection as HTMLElement).style.display = '';
        this.showElementSelectionGuidance();
      }
    } else {
      if (actionSection) {
        (actionSection as HTMLElement).style.display = '';
        this.hideElementSelectionGuidance();
      }
    }
  }

  private showElementSelectionGuidance(): void {
    const statusBar = this.element.querySelector('[data-ce="status"]');

    if (statusBar) {
      const actionSection =
        this.element.querySelector('[data-ce-wrap="value"]')?.parentElement?.parentElement;
      if (actionSection) {
        const actionSections = actionSection.querySelectorAll(
          '.ce-section:not(:has([data-ce="list"]))',
        );
        actionSections.forEach((section) => ((section as HTMLElement).style.display = 'none'));

        const addBtnRow = actionSection.querySelector('.ce-row:has([data-ce="add"])');
        if (addBtnRow) {
          (addBtnRow as HTMLElement).style.display = 'none';
        }

        const attrWrap = this.element.querySelector('[data-ce-wrap="attr"]');
        if (attrWrap) {
          (attrWrap as HTMLElement).style.display = 'none';
        }

        const valueWrap = this.element.querySelector('[data-ce-wrap="value"]');
        if (valueWrap) {
          (valueWrap as HTMLElement).style.display = 'none';
        }

        const imageWrap = this.element.querySelector('[data-ce-wrap="image"]');
        if (imageWrap) {
          (imageWrap as HTMLElement).style.display = 'none';
        }
      }

      if (!this.element.querySelector('.ce-element-guidance')) {
        const guidance = document.createElement('div');
        guidance.className = 'ce-element-guidance';
        guidance.innerHTML = `
          <div class="ce-guidance-content">
            <div class="ce-guidance-icon">👆</div>
            <div class="ce-guidance-text">
              <div class="ce-guidance-title">Select an element first</div>
              <div class="ce-guidance-subtitle">Click on any text, button, or image on the page to start editing</div>
            </div>
          </div>
        `;

        statusBar.insertAdjacentElement('afterend', guidance);
      }
    }
  }

  private hideElementSelectionGuidance(): void {
    const guidance = this.element.querySelector('.ce-element-guidance');
    if (guidance) {
      guidance.remove();
    }

    const actionSection =
      this.element.querySelector('[data-ce-wrap="value"]')?.parentElement?.parentElement;
    if (actionSection) {
      const sections = actionSection.querySelectorAll('.ce-section');
      sections.forEach((section) => ((section as HTMLElement).style.display = ''));

      const addBtnRow = actionSection.querySelector('.ce-row:has([data-ce="add"])');
      if (addBtnRow) {
        (addBtnRow as HTMLElement).style.display = '';
      }
    }
  }

  deselectElementAndResetForm(): void {
    const selectorInput = this.element.querySelector('[data-ce="selector"]') as HTMLInputElement;
    if (selectorInput) {
      selectorInput.value = '';
    }

    this.updateElementPreview('');
    this.updateUIStateForElementSelection(false);

    const actionSelect = this.element.querySelector('[data-ce="action"]') as HTMLSelectElement;
    if (actionSelect) {
      actionSelect.value = 'set_text';
    }

    const valueInput = this.element.querySelector('[data-ce="value"]') as HTMLTextAreaElement;
    if (valueInput) {
      valueInput.value = '';
    }

    this.hideAllFields();
    this.clearImageUploadToken();
    this.updateImagePreview(null, null);
  }

  public hideAllFields(): void {
    const valueWrap = this.element.querySelector('[data-ce-wrap="value"]');
    if (valueWrap) {
      (valueWrap as HTMLElement).style.display = 'none';
    }

    const imageWrap = this.element.querySelector('[data-ce-wrap="image"]');
    if (imageWrap) {
      (imageWrap as HTMLElement).style.display = 'none';
    }

    const attrWrap = this.element.querySelector('[data-ce-wrap="attr"]');
    if (attrWrap) {
      (attrWrap as HTMLElement).style.display = 'none';
    }
  }

  private updateElementPreview(selector: string): void {
    const preview = this.element.querySelector('[data-ce="element-preview"]');
    if (!preview) return;

    if (!selector) {
      preview.innerHTML = `
        <div class="ce-element-info">
          <div class="ce-element-type">📄 Page element</div>
          <div class="ce-element-name">Click on page to select</div>
        </div>
      `;
      return;
    }

    // Try to get element info
    try {
      const element = document.querySelector(selector);
      if (element) {
        const tagName = element.tagName.toLowerCase();
        const textContent = element.textContent?.slice(0, 30) || '';
        const elementType = this.getElementTypeDisplay(tagName);

        preview.innerHTML = `
          <div class="ce-element-info">
            <div class="ce-element-type">${sanitizeText(elementType)}</div>
            <div class="ce-element-name">${sanitizeText(textContent || tagName)}</div>
          </div>
        `;
      } else {
        preview.innerHTML = `
          <div class="ce-element-info">
            <div class="ce-element-type">❓ Unknown element</div>
            <div class="ce-element-name">${sanitizeText(selector)}</div>
          </div>
        `;
      }
    } catch (error) {
      preview.innerHTML = `
        <div class="ce-element-info">
          <div class="ce-element-type">❓ Element not found</div>
          <div class="ce-element-name">${sanitizeText(selector)}</div>
        </div>
      `;
    }
  }

  private getElementTypeDisplay(tagName: string): string {
    const typeMap: { [key: string]: string } = {
      h1: '📰 Page Title',
      h2: '📑 Section Title',
      h3: '📋 Subtitle',
      p: '📝 Paragraph',
      button: '🔘 Button',
      a: '🔗 Link',
      img: '🖼️ Image',
      div: '📦 Container',
      span: '🏷️ Text',
      input: '⌨️ Input Field',
      textarea: '📄 Text Area',
      select: '📋 Dropdown',
      ul: '📝 Bullet List',
      ol: '📝 Numbered List',
      li: '📄 List Item',
      nav: '🧭 Navigation',
      header: '📋 Header',
      footer: '📋 Footer',
      main: '📋 Main Content',
      section: '📦 Section',
      article: '📰 Article',
    };

    return typeMap[tagName] || `📄 ${tagName.toUpperCase()}`;
  }

  public updateActionFields(): void {
    const actionSelect = this.element.querySelector('[data-ce="action"]') as HTMLSelectElement;
    if (!actionSelect) return;

    const action = actionSelect.value;
    const needsValue = this.actionNeedsValue(action);
    const needsImage = this.actionNeedsImage(action);

    const valueWrap = this.element.querySelector('[data-ce-wrap="value"]');
    const valueLabel = this.element.querySelector('[data-ce-label="value"]');
    const valueInput = this.element.querySelector('[data-ce="value"]') as HTMLTextAreaElement;

    if (valueWrap && valueLabel && valueInput) {
      if (needsValue) {
        (valueWrap as HTMLElement).style.display = '';
        valueInput.disabled = false;
        valueLabel.textContent = this.valueLabelForAction(action);
        valueInput.placeholder = this.valuePlaceholderForAction(action);
      } else {
        (valueWrap as HTMLElement).style.display = 'none';
        valueInput.disabled = true;
        valueInput.value = '';
      }
    }

    const imageWrap = this.element.querySelector('[data-ce-wrap="image"]');
    if (imageWrap) {
      if (needsImage) {
        (imageWrap as HTMLElement).style.display = '';
      } else {
        (imageWrap as HTMLElement).style.display = 'none';
      }
    }

    const attrWrap = this.element.querySelector('[data-ce-wrap="attr"]');
    if (attrWrap) {
      (attrWrap as HTMLElement).style.display = 'none';
    }
  }

  private actionNeedsValue(action: string): boolean {
    return action === 'set_text';
  }

  private actionNeedsImage(action: string): boolean {
    return action === 'replace_image';
  }

  private valueLabelForAction(action: string): string {
    switch (action) {
      case 'set_text':
        return 'Text';
      default:
        return 'Value';
    }
  }

  private valuePlaceholderForAction(action: string): string {
    switch (action) {
      case 'set_text':
        return 'Enter new text…';
      default:
        return 'New value…';
    }
  }

  private clearImageUploadToken(): void {
    delete this.element.dataset.ceImageToken;
  }

  private updateImagePreview(imageUrl: string | null, fileName: string | null): void {
    const preview = this.element.querySelector('[data-ce="image-preview"]') as HTMLElement;
    const previewImg = this.element.querySelector('[data-ce="preview-img"]') as HTMLImageElement;
    const imageName = this.element.querySelector('[data-ce="image-name"]') as HTMLElement;
    const uploadArea = this.element.querySelector('[data-ce="upload-area"]') as HTMLElement;

    if (imageUrl && fileName && previewImg && imageName && uploadArea) {
      previewImg.src = imageUrl;
      imageName.textContent = fileName;
      (preview as HTMLElement).style.display = '';
      (uploadArea as HTMLElement).style.display = 'none';
    } else if (preview && uploadArea) {
      (preview as HTMLElement).style.display = 'none';
      (uploadArea as HTMLElement).style.display = '';
    }
  }
}
